/// ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/// Browser-side RSA keypair generation.
///
/// Centralized so any feature that needs to mint a fresh RSA keypair
/// for a user (Snowflake system user, future webhook signing keys,
/// custom JWT issuers, etc.) consumes the same WebCrypto-backed path
/// and gets the same export formats. The original home of this code
/// was `scripts/setup/buildSystemUserDdl.ts`; that file now imports
/// from here.
///
/// Format choices (the de-facto interop standard for RSA, which is
/// what most consumers — Snowflake, OpenSSL, Python's cryptography,
/// Java's KeyFactory — expect by default):
///
///   - **Public key**: base64 of the SubjectPublicKeyInfo (SPKI) DER.
///     Single line, no PEM headers. Snowflake's `RSA_PUBLIC_KEY`
///     accepts this verbatim. Wrap in PEM yourself with
///     `formatSpkiPem()` if a consumer needs the headered form.
///   - **Private key**: PKCS#8 PEM. Loads via Python cryptography's
///     `load_pem_private_key` and Snowflake's keypair JWT signer
///     unchanged.
///
/// WebCrypto-related notes:
///   - We pick `RSASSA-PKCS1-v1_5` + SHA-256 at generation. The
///     algorithm name only matters for sign/verify operations done
///     *within* WebCrypto itself; once exported, the key is just an
///     RSA key and any RSA implementation can load it for any RSA
///     scheme. Re-import with a different algorithm name if you
///     plan to sign within the browser using PSS instead.
///   - `extractable: true` is required so we can `exportKey('pkcs8')`
///     to PEM. Throws if the platform refuses extractability — modern
///     browsers don't, but worth surfacing rather than silently
///     producing garbage.
///   - Public key fingerprint (SHA-256 of SPKI) for things like
///     Snowflake KEYPAIR_JWT lives in `scripts/snowflake/signKeypairJwt.ts`
///     because that fingerprint format is Snowflake-specific (uppercase
///     account+user, base64 of the SHA-256 digest). Generation here
///     stays generic.
/// ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export interface GeneratedRsaKeyPair {
  /** Base64-encoded SPKI DER. Drop straight into Snowflake's `RSA_PUBLIC_KEY = '...'`. */
  publicKeyB64: string
  /** PEM-formatted PKCS#8 private key. Encrypt before storing. */
  privateKeyPem: string
}

export interface GenerateRsaKeypairOptions {
  /**
   * Modulus length in bits. Defaults to 2048 — the Snowflake-recommended
   * minimum and a sane default for everything else. Use 3072/4096 only
   * when a downstream system explicitly requires it; the bigger the
   * key, the slower every signing operation that uses it.
   */
  modulusLength?: 2048 | 3072 | 4096
  /**
   * The WebCrypto algorithm name baked into the generated `CryptoKey`.
   * Only matters if you plan to sign WITHIN WebCrypto immediately —
   * once exported (the typical path), the key is just RSA and works
   * with any algorithm. Defaults to `'RSASSA-PKCS1-v1_5'` because
   * that's what Snowflake's keypair-JWT signing uses.
   */
  signAlgorithm?: 'RSASSA-PKCS1-v1_5' | 'RSA-PSS'
}

/**
 * Generate a fresh RSA keypair via WebCrypto and export both halves
 * in the canonical interop formats (SPKI base64 public, PKCS#8 PEM
 * private).
 *
 * @throws if WebCrypto isn't available (non-browser context, or
 *         insecure http:// origin in some browsers) or if the
 *         platform refuses `extractable: true` on RSA keys.
 *
 * @example
 *   const { publicKeyB64, privateKeyPem } = await generateRsaKeypair()
 *   await registerPublicKeyWithSnowflake(publicKeyB64)
 *   await encryptAndStorePrivateKey(privateKeyPem)
 */
export async function generateRsaKeypair(options: GenerateRsaKeypairOptions = {}): Promise<GeneratedRsaKeyPair> {
  if (typeof window === 'undefined' || !window.crypto?.subtle) {
    throw new Error('WebCrypto is not available in this browser.')
  }

  const modulusLength = options.modulusLength ?? 2048
  const signAlgorithm = options.signAlgorithm ?? 'RSASSA-PKCS1-v1_5'

  const keyPair = await window.crypto.subtle.generateKey(
    {
      name: signAlgorithm,
      modulusLength,
      publicExponent: new Uint8Array([1, 0, 1]),
      hash: 'SHA-256',
    },
    /* extractable */ true,
    ['sign', 'verify'],
  )

  // Public key — Build the SPKI ourselves from the JWK's `n` + `e`
  // rather than using WebCrypto's `exportKey('spki')`. The browser's
  // SPKI bytes can subtly differ from what OpenSSL / Python's
  // cryptography produce — most commonly the explicit NULL
  // AlgorithmIdentifier parameters byte being absent versus present.
  // Different SPKI bytes → different SHA-256 fingerprint → Snowflake
  // rejects the JWT as "JWT token is invalid" when a user later
  // signs with snowsql or any other openssl-derived stack because
  // their fingerprint won't match the one Snowflake stored from our
  // registration. Canonicalizing here guarantees byte-for-byte parity
  // with `openssl rsa -pubout` output.
  const publicJwk = await window.crypto.subtle.exportKey('jwk', keyPair.publicKey)
  const publicKeyB64 = buildCanonicalRsaSpkiB64(publicJwk)

  // Private key — PKCS#8 → PEM with the standard header/footer + 64-char wrap.
  // PKCS#8 output from WebCrypto is identical to `openssl pkcs8 -topk8
  // -nocrypt` output (unencrypted PrivateKeyInfo) and loads cleanly
  // into snowsql / Python's `load_pem_private_key`. We do NOT encrypt
  // the PEM at rest — the operator is expected to either hand it
  // directly to the user over a secure channel, or encrypt it via
  // `openssl pkcs8 -topk8 -v2 des3` themselves once they receive it.
  const privateKeyPkcs8 = await window.crypto.subtle.exportKey('pkcs8', keyPair.privateKey)
  const privateKeyB64 = arrayBufferToBase64(privateKeyPkcs8)
  const privateKeyPem = wrapAsPem('PRIVATE KEY', privateKeyB64)

  return { publicKeyB64, privateKeyPem }
}

/**
 * Build the canonical X.509 SubjectPublicKeyInfo (SPKI) DER for an RSA
 * key from a JWK and return it as a single-line base64 string.
 *
 * The output is byte-identical to what `openssl rsa -pubout -outform
 * DER | base64` produces, which is what Snowflake (and every other
 * keypair-JWT-aware tool) expects when computing the public-key
 * fingerprint. WebCrypto's own `exportKey('spki')` is mostly
 * compatible but has historically varied on a couple of optional
 * encoding choices (most often the explicit `NULL` parameters byte
 * on the AlgorithmIdentifier). Generating the bytes ourselves
 * eliminates that variance.
 *
 * Throws if the JWK is missing `n` or `e` (i.e. it's not an RSA
 * public key).
 */
export function buildCanonicalRsaSpkiB64(jwk: JsonWebKey): string {
  return arrayBufferToBase64(buildCanonicalRsaSpkiFromJwk(jwk).buffer as ArrayBuffer)
}

export function buildCanonicalRsaSpkiFromJwk(jwk: JsonWebKey): Uint8Array {
  if (jwk.kty !== 'RSA' || !jwk.n || !jwk.e) {
    throw new Error('JWK is not an RSA key (missing n or e).')
  }
  const n = base64UrlToBytes(jwk.n)
  const e = base64UrlToBytes(jwk.e)

  // ASN.1 / DER assembly:
  //
  //   SubjectPublicKeyInfo ::= SEQUENCE {
  //       algorithm SEQUENCE {
  //           algorithm OBJECT IDENTIFIER (rsaEncryption = 1.2.840.113549.1.1.1),
  //           parameters NULL
  //       },
  //       subjectPublicKey BIT STRING (
  //           SEQUENCE { modulus INTEGER, publicExponent INTEGER }
  //       )
  //   }
  //
  // Per RFC 3279 §2.3.1 the AlgorithmIdentifier for `rsaEncryption`
  // MUST carry an explicit NULL parameters value (not absent).
  // Several Snowflake-adjacent tools rely on that for fingerprint
  // computation; omitting it changes the SHA-256 hash.
  const rsaPublicKey = derSequence(derInteger(n), derInteger(e))
  const algorithm = derSequence(derOid(RSA_ENCRYPTION_OID), derNull())
  return derSequence(algorithm, derBitString(rsaPublicKey))
}

/**
 * Wrap a raw SPKI base64 string as a `-----BEGIN PUBLIC KEY-----` PEM
 * block. Useful when a consumer expects the headered form rather than
 * the bare base64 (e.g. some Java tooling, OpenSSL CLI piping).
 */
export function formatSpkiPem(publicKeyB64: string): string {
  return wrapAsPem('PUBLIC KEY', publicKeyB64)
}

/**
 * Convert an `ArrayBuffer` (e.g. the output of `exportKey('spki')` or
 * `exportKey('pkcs8')`) to a base64 string. Exposed for callers that
 * need to handle binary key material outside this module.
 *
 * Built in chunks to dodge the V8 call-stack limit on large buffers.
 * RSA-2048 keys are tiny (~300 bytes SPKI / ~1.2 KB PKCS#8) — the
 * chunking is overkill at this scale but free and helps if the
 * helper grows new uses.
 */
export function arrayBufferToBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf)
  let binary = ''
  const chunk = 0x8000
  for (let i = 0; i < bytes.byteLength; i += chunk) {
    binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + chunk)) as any)
  }
  return btoa(binary)
}

// -----------------------------------------------------------------
// Internals
// -----------------------------------------------------------------

/**
 * Wrap a base64 body in the canonical PEM envelope: BEGIN/END markers
 * around base64 broken into 64-char lines. Used for both `PRIVATE KEY`
 * (PKCS#8) and `PUBLIC KEY` (SPKI) blocks.
 */
function wrapAsPem(label: 'PRIVATE KEY' | 'PUBLIC KEY', b64: string): string {
  const chunks = b64.match(/.{1,64}/g) || [b64]
  return `-----BEGIN ${label}-----\n${chunks.join('\n')}\n-----END ${label}-----`
}

// =================================================================
// Minimal ASN.1 DER encoder. Only the constructs needed for an RSA
// SubjectPublicKeyInfo are implemented (SEQUENCE, INTEGER, BIT STRING,
// OBJECT IDENTIFIER, NULL + DER length encoding). A general-purpose
// ASN.1 library would be overkill — this is ~60 lines, fully tested
// against `openssl asn1parse`, and stays local to the keypair flow.
// =================================================================

/** OID body bytes for `rsaEncryption` (1.2.840.113549.1.1.1).
 *  Encoded per X.690 base-128: first byte = 40*arc1 + arc2 = 42, then
 *  each subsequent arc as 7-bit big-endian with high-bit continuation. */
const RSA_ENCRYPTION_OID = new Uint8Array([
  0x2A, 0x86, 0x48, 0x86, 0xF7, 0x0D, 0x01, 0x01, 0x01,
])

/** DER length encoding per X.690 §8.1.3.
 *  Short form (<128): single byte. Long form: 0x80|n followed by n
 *  big-endian length bytes. */
function derLength(len: number): Uint8Array {
  if (len < 0x80) return new Uint8Array([len])
  if (len < 0x100) return new Uint8Array([0x81, len])
  if (len < 0x10000) return new Uint8Array([0x82, (len >> 8) & 0xFF, len & 0xFF])
  if (len < 0x1000000) {
    return new Uint8Array([0x83, (len >> 16) & 0xFF, (len >> 8) & 0xFF, len & 0xFF])
  }
  return new Uint8Array([
    0x84,
    (len >>> 24) & 0xFF, (len >> 16) & 0xFF, (len >> 8) & 0xFF, len & 0xFF,
  ])
}

/** Generic TLV (Tag + Length + Value). */
function derTlv(tag: number, content: Uint8Array): Uint8Array {
  const lenBytes = derLength(content.length)
  const out = new Uint8Array(1 + lenBytes.length + content.length)
  out[0] = tag
  out.set(lenBytes, 1)
  out.set(content, 1 + lenBytes.length)
  return out
}

/** INTEGER (tag 0x02). Canonical form requires a leading 0x00 byte
 *  when the high bit of the first content byte is set (so ASN.1
 *  treats it as positive rather than two's-complement-negative).
 *  Strip any extraneous leading zeros first. */
function derInteger(bytes: Uint8Array): Uint8Array {
  let start = 0
  while (start < bytes.length - 1 && bytes[start] === 0) start++
  let trimmed = bytes.subarray(start)
  if (trimmed.length > 0 && (trimmed[0] & 0x80) !== 0) {
    const prefixed = new Uint8Array(trimmed.length + 1)
    prefixed[0] = 0
    prefixed.set(trimmed, 1)
    trimmed = prefixed
  }
  return derTlv(0x02, trimmed)
}

/** SEQUENCE (tag 0x30 — constructed). */
function derSequence(...parts: Uint8Array[]): Uint8Array {
  const total = parts.reduce((sum, p) => sum + p.length, 0)
  const merged = new Uint8Array(total)
  let offset = 0
  for (const p of parts) {
    merged.set(p, offset)
    offset += p.length
  }
  return derTlv(0x30, merged)
}

/** NULL (tag 0x05) — always exactly `0x05 0x00`. */
function derNull(): Uint8Array {
  return new Uint8Array([0x05, 0x00])
}

/** OBJECT IDENTIFIER (tag 0x06) — caller supplies pre-encoded body. */
function derOid(rawOidBytes: Uint8Array): Uint8Array {
  return derTlv(0x06, rawOidBytes)
}

/** BIT STRING (tag 0x03). The first content byte is the "unused
 *  bits" count; for byte-aligned content (always the case here, the
 *  payload is a complete SEQUENCE) it's 0x00. */
function derBitString(content: Uint8Array): Uint8Array {
  const wrapped = new Uint8Array(content.length + 1)
  wrapped[0] = 0
  wrapped.set(content, 1)
  return derTlv(0x03, wrapped)
}

/** base64url → bytes. JWKs encode `n` / `e` in unpadded base64url. */
function base64UrlToBytes(s: string): Uint8Array {
  let normalized = s.replace(/-/g, '+').replace(/_/g, '/')
  const padNeeded = (4 - (normalized.length % 4)) % 4
  normalized += '='.repeat(padNeeded)
  const binary = atob(normalized)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return bytes
}
